import React, { useState } from 'react';
import { Badge } from '../ui/badge';

const COLS = [
  { key: 'drafting', label: 'Drafting', dot: 'bg-slate-400' },
  { key: 'review', label: 'Review', dot: 'bg-amber-400' },
  { key: 'submitted', label: 'Submitted', dot: 'bg-sky-500' },
  { key: 'closed', label: 'Closed', dot: 'bg-emerald-500' },
];
const stageOf = (p) => (['won', 'lost'].includes(p.outcome) ? 'closed' : (p.stage || 'drafting'));
const confW = (p) => (p.outcome === 'won' ? 1 : p.outcome === 'lost' ? 0
  : (p.response_score?.overall?.confidence_score ?? 50) / 100);
const fmtVal = (v, cur) => (v == null ? null
  : `${cur || 'AUD'} ${v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}K` : v}`);

const daysInStage = (p) => {
  const since = p.stage_changed_at || p.created_at;
  return since ? Math.floor((Date.now() - new Date(since).getTime()) / 86400000) : 0;
};

const scoreChipCls = (p) => {
  const nums = (p.scoring_scheme?.levels || []).map(l => parseFloat(l.label)).filter(n => !Number.isNaN(n));
  const lo = nums.length > 1 ? Math.min(...nums) : 0;
  const hi = nums.length > 1 ? Math.max(...nums) : 5;
  const t = Math.min(1, Math.max(0, (p.client_score_avg - lo) / (hi - lo || 1)));
  return t >= 0.66 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : t >= 0.33 ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-rose-50 text-rose-700 border-rose-200';
};

export const PipelineBoard = ({ projects, onOpenProject, onMove, thresholds = { warn: 7, stall: 14 } }) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  return (
    <div className="space-y-2" data-testid="rfp-pipeline-board">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {COLS.map(col => {
          const items = projects.filter(p => stageOf(p) === col.key);
          const droppable = col.key !== 'closed';
          const totals = items.reduce((acc, p) => {
            if (p.deal_value) {
              const c = p.currency || 'AUD';
              acc[c] = (acc[c] || 0) + p.deal_value;
            }
            return acc;
          }, {});
          const totalStr = Object.entries(totals).map(([c, v]) => fmtVal(v, c)).join(' · ');
          const expected = items.reduce((acc, p) => {
            if (p.deal_value) {
              const c = p.currency || 'AUD';
              acc[c] = (acc[c] || 0) + p.deal_value * confW(p);
            }
            return acc;
          }, {});
          const expStr = col.key === 'closed' ? ''
            : Object.entries(expected).map(([c, v]) => fmtVal(Math.round(v), c)).join(' · ');
          return (
            <div key={col.key}
              onDragOver={droppable ? (e) => { e.preventDefault(); setOverCol(col.key); } : undefined}
              onDragLeave={droppable ? () => setOverCol(c => (c === col.key ? null : c)) : undefined}
              onDrop={droppable ? (e) => { e.preventDefault(); setOverCol(null); if (dragId) onMove(dragId, col.key); setDragId(null); } : undefined}
              className={`rounded-md border p-2 min-h-[220px] transition-colors ${overCol === col.key ? 'border-[#4ABFCF] bg-[#4ABFCF]/5' : 'border-slate-200 bg-slate-50/50'}`}
              data-testid={`rfp-pipe-col-${col.key}`}>
              <p className="text-[11px] font-bold text-[#0F2040] uppercase tracking-wide flex items-center gap-1.5 mb-2 px-1">
                <span className={`w-2 h-2 rounded-full ${col.dot}`} />{col.label}
                <span className="text-slate-400 font-medium" data-testid={`rfp-pipe-count-${col.key}`}>{items.length}</span>
                {totalStr && (
                  <span className="ml-auto text-[9px] text-slate-500 normal-case" title="Value at stake in this column"
                    data-testid={`rfp-pipe-value-${col.key}`}>{totalStr}</span>
                )}
              </p>
              {totalStr && expStr && (
                <p className="text-[9px] text-[#0F8A9E] font-semibold px-1 -mt-1.5 mb-1.5"
                  title="Expected revenue — deal value weighted by response confidence (unscored deals counted at 50%)"
                  data-testid={`rfp-pipe-expected-${col.key}`}>expected: {expStr}</p>
              )}
              {items.map(p => (
                <div key={p.id} draggable={droppable}
                  onDragStart={() => setDragId(p.id)} onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => onOpenProject(p.id)}
                  className={`bg-white border border-slate-200 rounded-md p-2 mb-2 hover:border-[#4ABFCF] transition-colors ${droppable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
                  data-testid={`rfp-pipe-card-${p.id}`}>
                  <p className="text-xs font-semibold text-[#0F2040] truncate">{p.name}</p>
                  <p className="text-[9px] text-slate-400 truncate">{p.created_by}</p>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {p.deal_value != null && (
                      <Badge variant="outline" className="text-[8px] bg-[#0F2040]/5 text-[#0F2040] border-slate-200 font-semibold"
                        data-testid={`rfp-pipe-card-value-${p.id}`}>{fmtVal(p.deal_value, p.currency)}</Badge>
                    )}
                    {p.client_score_avg != null && (
                      <Badge variant="outline" className={`text-[8px] font-semibold ${scoreChipCls(p)}`}
                        title="Average score on the client's own scoring scale — weak-fit bids show red"
                        data-testid={`rfp-pipe-card-cscore-${p.id}`}>CS {p.client_score_avg}</Badge>
                    )}
                    {p.due_date && (
                      <Badge variant="outline" className="text-[8px] bg-slate-50 text-slate-500 border-slate-200">due {p.due_date}</Badge>
                    )}
                    {droppable && p.open_questions > 0 && (
                      <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-700 border-amber-200">{p.open_questions} open</Badge>
                    )}
                    {droppable && daysInStage(p) >= thresholds.warn && (
                      <Badge variant="outline" title={`In ${col.label} since ${new Date(p.stage_changed_at || p.created_at).toLocaleDateString()}`}
                        className={`text-[8px] font-semibold ${daysInStage(p) >= thresholds.stall ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                        data-testid={`rfp-pipe-age-${p.id}`}>
                        {daysInStage(p) >= thresholds.stall ? `stalled ${daysInStage(p)}d` : `in stage ${daysInStage(p)}d`}
                      </Badge>
                    )}
                    {col.key === 'closed' && (
                      <Badge variant="outline" className={`text-[8px] uppercase font-bold ${p.outcome === 'won' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>{p.outcome}</Badge>
                    )}
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="text-[10px] text-slate-300 text-center pt-6">Empty</p>}
            </div>
          );
        })}
      </div>
      <p className="text-[9px] text-slate-400" data-testid="rfp-pipe-hint">Drag a card between Drafting, Review and Submitted. Deals move to Closed when you record a Won/Lost outcome inside the project.</p>
    </div>
  );
};
